import type { AudioManager } from '../../audio/AudioManager';
import type { TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawButton, pointInRect, roundRect } from '../../render/ui/UI';
import { PERSISTENT_UNLOCKS, type PersistentUnlock } from '../../data/unlocks';
import type { PersistentSave, PersistentUnlockId, Rect } from '../../types';
import type { Phase } from '../Phase';

type Tab = 'menu' | 'smithy';

const NEW_RUN_BUTTON: Rect = { x: 60, y: 380, w: LOGICAL_WIDTH - 120, h: 56 };
const SMITHY_BUTTON: Rect = { x: 60, y: 460, w: LOGICAL_WIDTH - 120, h: 48 };
const BACK_BUTTON: Rect = { x: 24, y: 568, w: 100, h: 40 };

interface UnlockSlot {
  unlock: PersistentUnlock;
  rect: Rect;
}

export class MetaPhase implements Phase {
  private save: PersistentSave;
  private audio: AudioManager;
  private tab: Tab = 'menu';
  private done = false;
  private unbind: (() => void) | null = null;
  private unlockSlots: UnlockSlot[] = [];
  private scrollY = 0;
  private onSave: (s: PersistentSave) => void;
  private startNewRun = false;
  private elapsed = 0;
  private flash = 0;
  private flashMsg: string | null = null;

  constructor(
    save: PersistentSave,
    input: TouchInput,
    audio: AudioManager,
    onSave: (s: PersistentSave) => void,
  ) {
    this.save = save;
    this.audio = audio;
    this.onSave = onSave;
    this.unbind = input.onPointerDown((e) => this.handleDown(e));
  }

  private rebuildSlots(): void {
    this.unlockSlots = PERSISTENT_UNLOCKS.map((u, i) => ({
      unlock: u,
      rect: { x: 24, y: 130 + i * 80 - this.scrollY, w: LOGICAL_WIDTH - 48, h: 70 },
    }));
  }

  private handleDown(e: { pos: { x: number; y: number } }): void {
    if (this.done) return;
    if (this.tab === 'menu') {
      if (pointInRect(e.pos.x, e.pos.y, NEW_RUN_BUTTON)) {
        this.audio.play('weapon_complete');
        this.startNewRun = true;
        this.done = true;
        return;
      }
      if (pointInRect(e.pos.x, e.pos.y, SMITHY_BUTTON)) {
        this.audio.play('bellows_tap');
        this.tab = 'smithy';
        this.rebuildSlots();
        return;
      }
      return;
    }
    // smithy tab
    if (pointInRect(e.pos.x, e.pos.y, BACK_BUTTON)) {
      this.audio.play('bellows_tap');
      this.tab = 'menu';
      return;
    }
    for (const slot of this.unlockSlots) {
      if (pointInRect(e.pos.x, e.pos.y, slot.rect)) {
        this.tryPurchase(slot.unlock);
        return;
      }
    }
  }

  private tryPurchase(unlock: PersistentUnlock): void {
    if (this.save.unlockedUpgrades.includes(unlock.id)) return;
    if (unlock.grantsWeapon && this.save.unlockedWeapons.includes(unlock.grantsWeapon)) return;
    if (this.save.renown < unlock.renownCost) {
      this.audio.play('strike_miss');
      this.flashMsg = 'Not enough Renown';
      this.flash = 1.6;
      return;
    }
    this.save.renown -= unlock.renownCost;
    this.save.unlockedUpgrades.push(unlock.id);
    if (unlock.grantsWeapon) this.save.unlockedWeapons.push(unlock.grantsWeapon);
    this.onSave(this.save);
    this.audio.play('weapon_complete');
    this.flashMsg = `${unlock.name} unlocked!`;
    this.flash = 1.6;
  }

  update(dt: number): void {
    this.elapsed += dt;
    this.flash = Math.max(0, this.flash - dt);
  }

  isComplete(): boolean {
    return this.done;
  }

  isStartingRun(): boolean {
    return this.startNewRun;
  }

  dispose(): void {
    this.unbind?.();
    this.unbind = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const grad = ctx.createLinearGradient(0, 0, 0, LOGICAL_HEIGHT);
    grad.addColorStop(0, '#1a0f08');
    grad.addColorStop(0.5, '#241608');
    grad.addColorStop(1, '#0a0805');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    if (this.tab === 'menu') this.renderMenu(ctx);
    else this.renderSmithy(ctx);

    if (this.flash > 0 && this.flashMsg) {
      const alpha = Math.min(1, this.flash / 0.4);
      ctx.fillStyle = `rgba(255, 216, 74, ${alpha})`;
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(this.flashMsg, LOGICAL_WIDTH / 2, 110);
    }
  }

  private renderMenu(ctx: CanvasRenderingContext2D): void {
    // Title.
    ctx.fillStyle = '#f0d890';
    ctx.font = 'bold 38px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('BLADEWORKS', LOGICAL_WIDTH / 2, 110);
    ctx.fillStyle = '#a8957a';
    ctx.font = '13px system-ui, sans-serif';
    ctx.fillText('A smith\'s tale, one weapon at a time.', LOGICAL_WIDTH / 2, 158);

    // Renown badge.
    ctx.fillStyle = '#ffd84a';
    ctx.font = 'bold 14px system-ui, sans-serif';
    ctx.fillText(`Renown: ${this.save.renown}`, LOGICAL_WIDTH / 2, 200);

    // Stats summary.
    const s = this.save.stats;
    ctx.fillStyle = '#7a6a55';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText(
      `Runs completed: ${s.totalRunsCompleted}  ·  Heroes saved: ${s.totalHeroesSaved}  ·  Weapons forged: ${s.totalWeaponsForged}`,
      LOGICAL_WIDTH / 2,
      230,
    );

    drawButton(ctx, { rect: NEW_RUN_BUTTON, label: 'NEW RUN', enabled: true, highlight: true });
    drawButton(ctx, { rect: SMITHY_BUTTON, label: 'SMITHY', enabled: true });
  }

  private renderSmithy(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#f0d890';
    ctx.font = 'bold 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Smithy Upgrades', LOGICAL_WIDTH / 2, 28);
    ctx.fillStyle = '#ffd84a';
    ctx.font = '14px system-ui, sans-serif';
    ctx.fillText(`Renown: ${this.save.renown}`, LOGICAL_WIDTH / 2, 64);
    ctx.fillStyle = '#a8957a';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText('Spend Renown on permanent improvements.', LOGICAL_WIDTH / 2, 86);

    this.rebuildSlots();
    // Clip the unlock list so it doesn't paint over the back button.
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 110, LOGICAL_WIDTH, 440);
    ctx.clip();
    for (const slot of this.unlockSlots) {
      this.drawUnlockSlot(ctx, slot);
    }
    ctx.restore();

    drawButton(ctx, { rect: BACK_BUTTON, label: 'BACK', enabled: true });
  }

  private drawUnlockSlot(ctx: CanvasRenderingContext2D, slot: UnlockSlot): void {
    const owned = this.isUnlocked(slot.unlock.id);
    const canAfford = this.save.renown >= slot.unlock.renownCost;
    ctx.fillStyle = owned ? '#142010' : canAfford ? '#2a2420' : '#1a1714';
    roundRect(ctx, slot.rect.x, slot.rect.y, slot.rect.w, slot.rect.h, 8);
    ctx.fill();
    ctx.strokeStyle = owned ? '#5fa080' : canAfford ? '#7a6a55' : '#3a3530';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = owned ? '#7fffaa' : canAfford ? '#f0d890' : '#7a6a55';
    ctx.font = 'bold 14px system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(slot.unlock.name, slot.rect.x + 12, slot.rect.y + 10);

    ctx.fillStyle = owned ? '#5fa080' : canAfford ? '#c9b896' : '#5a5045';
    ctx.font = '11px system-ui, sans-serif';
    ctx.fillText(slot.unlock.description, slot.rect.x + 12, slot.rect.y + 32);

    ctx.textAlign = 'right';
    ctx.font = 'bold 13px system-ui, sans-serif';
    if (owned) {
      ctx.fillStyle = '#7fffaa';
      ctx.fillText('OWNED', slot.rect.x + slot.rect.w - 12, slot.rect.y + 12);
    } else {
      ctx.fillStyle = canAfford ? '#ffd84a' : '#7a6a55';
      ctx.fillText(`${slot.unlock.renownCost} R`, slot.rect.x + slot.rect.w - 12, slot.rect.y + 12);
    }
  }

  private isUnlocked(id: PersistentUnlockId): boolean {
    return this.save.unlockedUpgrades.includes(id);
  }
}
